import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';

const FooterEl = styled.footer`
  background: #0F0B05;
  padding: 80px 0 48px;
  border-top: 1px solid rgba(250,243,224,0.06);
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
`;

const Top = styled.div`
  display: grid;
  gap: 48px;
  margin-bottom: 64px;
  @media (min-width: 768px) {
    grid-template-columns: 1fr auto auto;
    align-items: start;
    gap: 80px;
  }
`;

const Brand = styled.div`
  h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 2rem;
    font-weight: 600;
    color: #FAF3E0;
    margin-bottom: 10px;
    letter-spacing: 0.01em;
  }
  p {
    font-size: 0.83rem;
    color: rgba(250,243,224,0.4);
    line-height: 1.6;
    max-width: 240px;
  }
`;

const Links = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  @media (min-width: 768px) { flex-direction: column; gap: 16px; }
  a {
    font-size: 0.83rem;
    color: rgba(250,243,224,0.45);
    letter-spacing: 0.03em;
    transition: color 0.2s;
    &:hover { color: #C07020; }
  }
`;const Socials = styled.div`
  display: flex;
  gap: 10px;
  @media (min-width: 768px) { flex-direction: column; }
`;

const SocialBtn = styled.a`
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(250,243,224,0.06);
  border: 1px solid rgba(250,243,224,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FAF3E0;
  transition: background 0.25s, border-color 0.25s, transform 0.2s;
  svg { width: 15px; height: 15px; fill: currentColor; }
  &:hover {
    background: #7C3A00;
    border-color: #7C3A00;
    transform: translateY(-2px);
  }
`;

const Divider = styled.div`
  border-top: 1px solid rgba(250,243,224,0.06);
  padding-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  @media (min-width: 768px) { flex-direction: row; justify-content: space-between; }
  p {
    font-size: 0.75rem;
    color: rgba(250,243,224,0.28);
    letter-spacing: 0.03em;
  }
`;

const links = [
  { path: '',         en: 'Home',           es: 'Inicio',             fr: 'Accueil'        },
  { path: '/about',   en: 'About Us',       es: 'Nosotros',           fr: 'À Propos'       },
  { path: '/store',   en: 'Store',          es: 'Tienda',             fr: 'Boutique'       },
  { path: '/contact', en: 'Contact',        es: 'Contacto',           fr: 'Contact'        },
  { path: '/terms',   en: 'Terms',          es: 'Términos',           fr: 'Conditions'     },
  { path: '/privacy', en: 'Privacy Notice', es: 'Aviso de Privacidad', fr: 'Confidentialité' },
];

export function Footer() {
  const { t } = useLanguage();
  const { region } = useRegion();

  return (
    <FooterEl>
      <Container>
        <Top>
          <Brand>
            <h3>Hacienda Real</h3>
            <p>{t('A tradition in every spoonful.', 'Una tradición en cada cucharada.', 'Une tradition à chaque cuillère.')}</p>
          </Brand>

          <Links>
            {links.map((l) => (
              <Link key={l.path} to={`/${region}${l.path}`}>{t(l.en, l.es, l.fr)}</Link>
            ))}
          </Links>

          <Socials>
            <SocialBtn href="https://www.instagram.com/cajetahaciendareal?igsh=b2lpM3FpZGZsNjZ6" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </SocialBtn>
            <SocialBtn href="https://www.tiktok.com/@cajetahaciendarea?_r=1&_t=ZS-95DUF6gHRAa" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
              <svg viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            </SocialBtn>
            <SocialBtn href="https://wa.me/" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </SocialBtn>
          </Socials>
        </Top>

        <Divider>
          <p>© 2025 Hacienda Real. {t('All rights reserved.', 'Todos los derechos reservados.', 'Tous droits réservés.')}</p>
          <p>{t('San Juan de los Lagos, Jalisco · Since 1950', 'San Juan de los Lagos, Jalisco · Desde 1950', 'San Juan de los Lagos, Jalisco · Depuis 1950')}</p>
        </Divider>
      </Container>
    </FooterEl>
  );
}
